/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { theme } from "@nordcloud/gnui";
import {
  EventActionAttempt,
  EventActionScope,
  EventDetailsFragment,
  EventStatus,
  ExecutionStatus,
  GetEventViewDocument,
  GetEventsViewDocument,
  GetIncomingEventDocument,
  GetIncomingEventsDocument,
  IncomingEventStatus,
  ResourceExecutionErrorCodeCount,
  ResourceExecutionStatus,
} from "~/generated/graphql";
import { inflect, isEmpty, isNotEmpty, isNotNil } from "~/tools";
import { IconName } from "~/types/IconName";
import { TooltipColor } from "~/types/TooltipColor";
import { hasLogRelatedErrorCodeCounts } from "~/utils/logErrorCode";
import type { EventResourceGroup } from "./EventsDetails/Tabs/Details/hooks/useEventResourceController/types";
import { EventEntity } from "./types";

export type Status = {
  description: string;
  icon: IconName;
  color: () => string;
  tooltipColor?: TooltipColor;
  pathColors?: Record<number, string>;
};

type EventStatusAction = {
  id: string;
  status?: string | null;
  globalState?: {
    status?: string | null;
  } | null;
  errorCodeCounts?: ResourceExecutionErrorCodeCount[] | null;
};

function hasSuccessfulActionWithAttention(actions: EventStatusAction[] = []) {
  return actions.some(
    (action) =>
      (action.globalState?.status ?? action.status) ===
        ExecutionStatus.Success &&
      hasLogAttentionErrorCodeCounts(action.errorCodeCounts ?? [])
  );
}

function getAttentionNeededSuccessStatus(statusDetails: Status): Status {
  return {
    ...statusDetails,
    description: "Successful. Attention needed",
    icon: "statusSuccessDetailed",
    pathColors: {
      1: theme.color.background.error,
    },
  };
}

export function getEventStatus(
  status?: string,
  actionsLength?: number
): Status {
  switch (status) {
    case EventStatus.WaitingForApproval:
      return {
        description: `Event has unapproved ${inflect("action")(
          actionsLength ?? 0
        )}`,
        icon: "pausedFilled",
        color: () => `${theme.color.support.purple}`,
        tooltipColor: TooltipColor.accent,
      };
    case EventStatus.Failed:
      return {
        description: "Failed",
        icon: "cancelFilled",
        color: () => `${theme.color.background.error}`,
        tooltipColor: TooltipColor.danger,
      };
    case IncomingEventStatus.Upcoming:
      return {
        description: "Upcoming",
        icon: "waitingFilled",
        color: () => `${theme.color.support.grey}`,
      };

    case EventStatus.InProgress:
      return {
        description: "In Progress",
        icon: "inProgressFilled",
        color: () => `${theme.color.background.info}`,
        tooltipColor: TooltipColor.notification,
      };

    case EventStatus.PartialSuccess:
      return {
        description: "Partially Successful",
        icon: "warningFilled",
        color: () => `${theme.color.background.warning}`,
        tooltipColor: TooltipColor.warning,
      };
    case IncomingEventStatus.Revoked:
      return {
        description: "Revoked",
        icon: "cancelFilled",
        color: () => `${theme.colors.primary}`,
      };

    case EventStatus.Success:
      return {
        description: "Successful",
        icon: "successFilled",
        color: () => `${theme.color.background.success}`,
        tooltipColor: TooltipColor.success,
      };

    default:
      return {
        description: "N/A",
        icon: "playFilled",
        color: () => `${theme.color.support.grey}`,
      };
  }
}

export function getEventStatusWithAttention({
  status,
  actionsLength,
  actions = [],
}: {
  status?: string;
  actionsLength?: number;
  actions?: EventStatusAction[];
}): Status {
  const statusDetails = getEventStatus(status, actionsLength);

  if (
    status === EventStatus.Success &&
    hasSuccessfulActionWithAttention(actions)
  ) {
    return getAttentionNeededSuccessStatus(statusDetails);
  }

  return statusDetails;
}

export function getEventActionBatchStatusWithAttention({
  status,
  actions = [],
}: {
  status?: string;
  actions?: EventStatusAction[];
}): Status {
  const statusDetails = getEventEntityStatus(status);

  if (
    status === ExecutionStatus.Success &&
    hasSuccessfulActionWithAttention(actions)
  ) {
    return getAttentionNeededSuccessStatus(statusDetails);
  }

  return statusDetails;
}

export function getEventEntityStatus(status?: string): Status {
  switch (status) {
    case ExecutionStatus.ApprovalRequired:
      return {
        description: "Unapproved",
        icon: "pausedFilled",
        color: () => `${theme.color.support.purple}`,
        tooltipColor: TooltipColor.accent,
      };
    case ExecutionStatus.ApprovalTimeout:
      return {
        description: "Approval Timed out",
        icon: "pausedFilled",
        color: () => `${theme.color.background.error}`,
        tooltipColor: TooltipColor.danger,
      };
    case ExecutionStatus.Failed:
      return {
        description: "Failed",
        icon: "cancelFilled",
        color: () => `${theme.color.background.error}`,
        tooltipColor: TooltipColor.danger,
      };

    case ExecutionStatus.InProgress:
      return {
        description: "In Progress",
        icon: "inProgressFilled",
        color: () => `${theme.color.background.info}`,
        tooltipColor: TooltipColor.notification,
      };

    case ExecutionStatus.PartialSuccess:
      return {
        description: "Partially Successful",
        icon: "warningFilled",
        color: () => `${theme.color.background.warning}`,
        tooltipColor: TooltipColor.warning,
      };

    case ExecutionStatus.Success:
      return {
        description: "Successful",
        icon: "successFilled",
        color: () => `${theme.color.background.success}`,
        tooltipColor: TooltipColor.success,
      };
    case ExecutionStatus.Timeout:
      return {
        description: "Timeout",
        icon: "timeOutFilled",
        color: () => `${theme.color.background.warning}`,
        tooltipColor: TooltipColor.warning,
      };
    case ExecutionStatus.Upcoming:
      return {
        description: "Upcoming",
        icon: "waitingFilled",
        color: () => `${theme.color.support.grey}`,
      };
    case IncomingEventStatus.Revoked:
      return {
        description: "Revoked",
        icon: "cancelFilled",
        color: () => `${theme.colors.primary}`,
      };
    default:
      return {
        description: "N/A",
        icon: "playFilled",
        color: () => `${theme.color.support.grey}`,
      };
  }
}

export function isFinishedEventStatus(
  status?: EventStatus | IncomingEventStatus
): boolean {
  if (!status) {
    return false;
  }

  switch (status) {
    case EventStatus.InProgress:
    case EventStatus.WaitingForApproval:
    case IncomingEventStatus.Upcoming:
      return false;
    case EventStatus.Failed:
    case EventStatus.PartialSuccess:
    case EventStatus.Success:
    case IncomingEventStatus.Revoked:
      return true;
  }

  const exhaustiveCheck: never = status;
  return exhaustiveCheck;
}

export function hasLogAttentionErrorCodeCounts(
  errorCodeCounts: ResourceExecutionErrorCodeCount[] = []
) {
  return hasLogRelatedErrorCodeCounts(errorCodeCounts);
}

export function getLatestAttemptSummaryId(eventEntity: {
  id: string;
  attempts?: EventActionAttempt[] | null;
}) {
  return eventEntity.attempts && eventEntity.attempts.length > 0
    ? eventEntity.attempts[eventEntity.attempts.length - 1].id
    : eventEntity.id;
}

export function getEventEntityStatusWithAttention({
  status,
  errorCodeCounts = [],
}: {
  status?: string;
  errorCodeCounts?: ResourceExecutionErrorCodeCount[];
}): Status {
  const statusDetails = getEventEntityStatus(status);

  if (
    status === ExecutionStatus.Success &&
    hasLogAttentionErrorCodeCounts(errorCodeCounts)
  ) {
    return getAttentionNeededSuccessStatus(statusDetails);
  }

  return statusDetails;
}

export function convertMinutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const minutesRemainder = minutes % 60;
  const HH = hours.toString().padStart(2, "0");
  const mm = minutesRemainder.toString().padStart(2, "0");
  return `${HH}:${mm}`;
}

export enum RefetchOptions {
  EVENTS = "EventsDocument",
  INCOMING_EVENTS = "GetIncomingEventsDocument",
  EVENT = "GetEventDocument",
  INCOMING_EVENT = "GetIncomingEventDocument",
}

export const refetchQueryHelper = (query: RefetchOptions) => {
  switch (query) {
    case RefetchOptions.EVENTS:
      return GetEventsViewDocument;
    case RefetchOptions.INCOMING_EVENTS:
      return GetIncomingEventsDocument;
    case RefetchOptions.EVENT:
      return GetEventViewDocument;
    case RefetchOptions.INCOMING_EVENT:
      return GetIncomingEventDocument;
    default:
      throw new Error("unsupported refetch option");
  }
};

export const isResourceScope = (eventAction: EventEntity) => {
  return eventAction?.scope === EventActionScope.Resource;
};

export function hasEventActionResources(eventEntity: EventEntity) {
  if (
    isNotNil(eventEntity.resourceGroups) &&
    isNotEmpty(eventEntity.resourceGroups)
  ) {
    return eventEntity.resourceGroups.some(
      (group) => group && (group.resourcesCounts?.totalCount ?? 0) > 0
    );
  }

  return isNotNil(eventEntity.resources) && isNotEmpty(eventEntity.resources);
}

export const shouldAwaitResourceStates = (
  eventEntity: EventEntity,
  resourceGroups?: EventResourceGroup[] | null
) => {
  return (
    isResourceScope(eventEntity) &&
    hasEventActionResources(eventEntity) &&
    (eventEntity.globalState?.status === ExecutionStatus.Upcoming ||
      eventEntity.globalState?.status === ExecutionStatus.InProgress) &&
    isNotNil(resourceGroups) &&
    isEmpty(resourceGroups)
  );
};

export const appendResourceStateGroups = (
  prevGroups: EventResourceGroup[],
  returnedGroups: EventResourceGroup[]
) => {
  const updatedPrevGroups =
    prevGroups.map((prevGroup) => {
      const matchingGroup = returnedGroups.find(
        (group) => group.resourceGroupId === prevGroup.resourceGroupId
      );

      return matchingGroup
        ? {
            ...prevGroup,
            resourceStates: [
              ...(prevGroup.resourceStates || []),
              ...(matchingGroup.resourceStates || []),
            ],
          }
        : prevGroup;
    }) || [];

  const newGroups = returnedGroups.filter(
    (newGroup) =>
      !prevGroups.some(
        (prevGroup) => prevGroup.resourceGroupId === newGroup.resourceGroupId
      )
  );

  return [...updatedPrevGroups, ...newGroups];
};

export const allResourcesFinished = (
  resourceGroups: EventResourceGroup[]
): boolean => {
  const allDone = resourceGroups.some((group) =>
    (group.resourceStates || []).some(
      (state) =>
        state.status === ResourceExecutionStatus.Pending ||
        state.status === ResourceExecutionStatus.InProgress
    )
  );

  return !allDone;
};

export const getNumberOfApprovalRequiredEntities = (
  event: EventDetailsFragment
) => {
  const approveActions =
    event.actions?.filter(
      (eventAction) =>
        eventAction.globalState?.status === ExecutionStatus.ApprovalRequired
    ) ?? [];

  const approveBatches =
    event?.actionBatches?.filter(
      (eventActionBatch) =>
        eventActionBatch.status === ExecutionStatus.ApprovalRequired
    ) ?? [];

  return approveActions?.length + approveBatches?.length;
};

export const getAllEventActionsFlat = (event: EventDetailsFragment) => {
  const eventActions = event.actions ?? [];

  const eventBatchedActions =
    event.actionBatches?.flatMap((batch) => batch.actions) ?? [];

  return [...eventActions, ...eventBatchedActions];
};
